import React, { useMemo, useState } from "react";
import { useTable } from "react-table";
import { Table } from "reactstrap";
import LeaderboardAwardPopup from "./LeaderboardAwardPopup";
import { useTranslation } from "react-i18next";

const getHeaderLabel = (leaderboardTypeName = "") => {
  switch (leaderboardTypeName) {
    case "Goal Progress":
      return "progress";
    case "Event Summary":
      return "awards(amount)";
    case "Event Volume":
      return "awards(count)";
    default:
      return "awards";
  }
};

const formatMetricValue = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "0";
  }

  return Math.round(numericValue).toLocaleString();
};

const mergeClassNames = (...parts) => parts.filter(Boolean).join(" ");

const LeaderboardTable = ({ leaderboard, variant = "participant" }) => {
  const { t } = useTranslation();
  const [participant, setParticipant] = useState(null);
  const [isOpen, setOpen] = useState(false);

  const toggle = () => {
    setOpen((previousState) => !previousState);
  };

  const handleClickRow = (row) => {
    setParticipant(row.original);
    toggle();
  };

  const headerLabel = getHeaderLabel(leaderboard?.leaderboard_type?.name);

  const columns = useMemo(
    () => [
      {
        Header: "rank",
        accessor: "ranking",
        className: "leaderboards-table__cell leaderboards-table__cell--rank",
        Cell: ({ value }) => {
          if (variant === "manager") {
            return <span className="leaderboards-table__rank-badge">{value || "-"}</span>;
          }

          return value || "-";
        },
      },
      {
        Header: "participant",
        accessor: "display_name",
        className: "leaderboards-table__cell leaderboards-table__cell--participant",
        Cell: ({ value }) => (
          <span className="link-hover leaderboards-table__participant-name">{value}</span>
        ),
      },
      {
        Header: headerLabel,
        accessor: "total",
        className: "leaderboards-table__cell leaderboards-table__cell--metric",
        Cell: ({ value }) => (
          <span className="leaderboards-table__metric-cell">
            <span className="leaderboards-table__metric-value">{formatMetricValue(value)}</span>
          </span>
        ),
      },
    ],
    [headerLabel, variant]
  );

  const data = leaderboard?.leaders || [];

  const { getTableProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  if (rows.length === 0) {
    return (
      <>
        <div className={`leaderboards-table__empty leaderboards-table__empty--${variant}`}>
          {t("no_activity_yet")}
        </div>
        <LeaderboardAwardPopup
          isOpen={isOpen}
          setOpen={setOpen}
          toggle={toggle}
          participant={participant}
          leaderboardId={leaderboard?.id}
        />
      </>
    );
  }

  return (
    <>
      <Table
        striped
        borderless
        size="md"
        className={`leaderboards-table leaderboards-table--${variant} table--stack-mobile`}
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  className={mergeClassNames(column.className, "leaderboards-table__head-cell")}
                >
                  {t(column.Header)}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row) => {
            prepareRow(row);

            return (
              <tr
                {...row.getRowProps()}
                className="leaderboards-table__row"
                onClick={() => handleClickRow(row)}
              >
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    className={mergeClassNames(cell.column.className, "leaderboards-table__body-cell")}
                    data-label={typeof cell.column.Header === "string" ? t(cell.column.Header) : ""}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
      <LeaderboardAwardPopup
        isOpen={isOpen}
        setOpen={setOpen}
        toggle={toggle}
        participant={participant}
        leaderboardId={leaderboard?.id}
      />
    </>
  );
};

export default LeaderboardTable;
